import {
  faAngleRight,
  faArrowLeft,
  faEdit,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import MuiDrawer from '@mui/material/Drawer';
import React, { useState, useTransition } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DaxkoLogo from 'src/assets/img/daxko-logo.svg';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { List, ListItem, ListItemProps } from 'src/components/ListItem';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Theme } from 'src/components/muiTheme/types';
import {
  ClickAwayListener,
  useMediaQuery,
} from 'src/components/muiTheme/utils';
import { useOptionalSession } from 'src/contexts/SessionContext';

import { ActionsMenu } from './ActionsMenu';
import { EditNavButton } from './EditNavButton';
import { getSolidIcon } from './routeConfig';
import { useAdminNav } from './useAdminNav';
import { DecoratedNavItem, useMainNav } from './useMainNav';

export interface MainNavProps {
  open: boolean;
  toggleOpen: () => void;
  customNavItems?: DecoratedNavItem[];
  navWidth?: string;
  isAdmin?: boolean;
}

export const MainNavDreamcoat: React.FC<MainNavProps> = ({
  open,
  toggleOpen,
  customNavItems,
  navWidth = '84px',
  isAdmin = false,
}) => {
  const [activeSubmenu, setActiveSubmenu] = useState<DecoratedNavItem | null>(
    null
  );
  const { session } = useOptionalSession();

  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return (
    <ClickAwayListener
      onClickAway={() => activeSubmenu && setActiveSubmenu(null)}
    >
      <MuiDrawer
        open={open}
        anchor="left"
        disableEnforceFocus
        PaperProps={{
          elevation: 0,
          sx: { bgcolor: 'transparent', p: 0 },
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: smDown ? '100%' : navWidth,
            height: '100%',
            p: 0,
            overflow: 'visible',
          },
          '&& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, .15)',
          },
        }}
        onClose={toggleOpen}
        variant={smDown ? 'temporary' : 'permanent'}
      >
        <Stack
          direction="column"
          sx={{
            width: smDown ? '100%' : navWidth,
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            zIndex: 2,
            position: 'relative',
            bgcolor: '#014271',
            overflowY: 'auto',
          }}
        >
          {smDown ? (
            <Stack
              justifyContent={'space-between'}
              sx={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2, py: 1.5 }}
                >
                  <Stack direction="row" alignItems="center">
                    <img
                      src={DaxkoLogo}
                      alt="Daxko Logo"
                      width="24px"
                      height="24px"
                    />
                    <Typography
                      color="white"
                      variant="h5"
                      fontWeight={400}
                      sx={{ ml: 1 }}
                      // eslint-disable-next-line i18next/no-literal-string
                    >
                      Exercise
                    </Typography>
                  </Stack>

                  <IconButton className="dark" onClick={toggleOpen}>
                    <Icon size="small" icon={faTimes} />
                  </IconButton>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2.5, pb: 2 }}
                >
                  <Typography color="white" variant="body1">
                    {session?.brand}
                  </Typography>

                  <div className="dark">
                    <ActionsMenu size="small" hideUserMenu />
                  </div>
                </Stack>

                <Scroller
                  direction="column"
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: 'auto',
                    maxHeight: 'none',
                    width: '100%',
                  }}
                >
                  {isAdmin ? (
                    <AdminNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                    />
                  ) : (
                    <MainNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                      customNavItems={customNavItems}
                    />
                  )}
                </Scroller>
              </Box>

              <Stack
                direction="column"
                alignItems="stretch"
                sx={{
                  boxShadow: '0px 0px 20px 0px #00000080',
                  py: 1,
                  flexShrink: 0,
                }}
              >
                <EditNavButton>
                  <TopLevelNavItem title="Edit Nav" icon={faEdit} />
                </EditNavButton>
              </Stack>
            </Stack>
          ) : (
            <>
              <Stack
                direction="column"
                alignItems="center"
                sx={{
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box sx={{ mt: 2, mb: 0.5 }}>
                  <img
                    src={DaxkoLogo}
                    alt="Daxko Logo"
                    width="40px"
                    height="40px"
                  />
                </Box>

                <Scroller
                  direction="column"
                  hideScrollbar
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: 'auto',
                    maxHeight: 'none',
                    overflowY: 'auto',
                    width: '100%',
                  }}
                >
                  {isAdmin ? (
                    <AdminNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                    />
                  ) : (
                    <MainNavItems
                      activeSubmenu={activeSubmenu}
                      setActiveSubmenu={setActiveSubmenu}
                      customNavItems={customNavItems}
                    />
                  )}
                </Scroller>
              </Stack>

              <Stack
                direction="column"
                alignItems="stretch"
                sx={{ boxShadow: '0px 0px 20px 0px #00000080', py: 1 }}
              >
                <EditNavButton>
                  <TopLevelNavItem title="Edit Nav" icon={faEdit} />
                </EditNavButton>
              </Stack>
            </>
          )}
        </Stack>

        <Stack
          sx={{
            bgcolor: '#232236',
            p: 1,
            transform: activeSubmenu ? 'translateX(0)' : 'translateX(-100%)',
            opacity: activeSubmenu ? 1 : 0,
            pointerEvents: activeSubmenu ? 'auto' : 'none',
            transition: 'all 0.2s ease-in-out',
            zIndex: smDown ? 4 : 1,
            position: 'absolute',
            top: '0px',
            left: smDown ? '0px' : navWidth,
            height: '100%',
            width: smDown ? '100%' : '280px',
          }}
        >
          {smDown ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1 }}
            >
              <Stack direction="row" alignItems="center" sx={{ ml: -1 }}>
                <IconButton
                  className="dark"
                  onClick={() => setActiveSubmenu(null)}
                >
                  <Icon size="small" icon={faArrowLeft} />
                </IconButton>
                <Typography color="white" variant="h5">
                  {activeSubmenu?.name}
                </Typography>
              </Stack>
              <IconButton
                className="dark"
                onClick={() => {
                  toggleOpen();
                  setActiveSubmenu(null);
                }}
              >
                <Icon size="small" icon={faTimes} />
              </IconButton>
            </Stack>
          ) : (
            <Box sx={{ p: 1 }}>
              <Typography color="white" variant="h4" sx={{ mt: -0.7 }}>
                {session?.brand}
              </Typography>
              <Typography color="white" variant="body1">
                {activeSubmenu?.name}
              </Typography>
            </Box>
          )}
          <Scroller direction="column" sx={{ mt: 2, maxHeight: '100%' }}>
            {activeSubmenu?.children?.map((subItem, i) => {
              return (
                <TopLevelNavItem
                  key={subItem.id + i}
                  title={subItem.name}
                  route={subItem.route}
                  reactRoute={subItem.reactRoute}
                  onClick={() => {
                    setActiveSubmenu(null);
                  }}
                  target={subItem.target}
                  rel={subItem.rel}
                  subItem
                ></TopLevelNavItem>
              );
            })}
          </Scroller>
        </Stack>
      </MuiDrawer>
    </ClickAwayListener>
  );
};

export interface TopLevelNavItemProps {
  title: string;
  route?: string;
  reactRoute?: string;
  icon?: ListItemProps['icon'];
  onClick?: () => void;
  subnavOpen?: boolean;
  target?: string;
  rel?: string;
  level?: number;
  subItem?: boolean;
  //setActiveSubmenu: (id: DecoratedNavItem | null) => void;
}

export const TopLevelNavItem: React.FC<TopLevelNavItemProps> = ({
  title,
  icon,
  route,
  reactRoute,
  onClick,
  subnavOpen,
  target,
  rel,
  subItem,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasChildrenRoutes = !route && !reactRoute;
  const location = useLocation();
  const navigate = useNavigate();
  // Only used when NavItem has children
  const [, startTransition] = useTransition();

  const { pathname } = location;
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const navItemStyles = () => {
    return {
      p: 0,
      py: 1.5,
      m: 0,
      display: 'flex',
      gap: 0.5,
      px: { xs: 2, sm: 0 },
      flexDirection: { xs: 'row', sm: 'column' },
      color: 'primary.contrastText',
      position: 'relative',
      '&&&& .MuiListItemIcon-root svg': {
        color: 'primary.contrastText',
        fontSize: '16px',
        mb: { xs: 0, sm: 0.5 },
      },
      '&&&& .MuiListItemText-root .MuiTypography-root': {
        color: 'primary.contrastText',
        fontSize: { xs: '16px', sm: '10px' },
        lineHeight: '1.2em',
        fontWeight: { xs: 400, sm: 600 },
        textAlign: { xs: 'left', sm: 'center' },
        px: 1,
      },
      '&.Mui-selected': {
        bgcolor: '#025B9C',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: '#038BED',
        ...(hasChildrenRoutes && {
          '*:after': {
            content: '""',
            position: 'absolute',
            top: 'calc(50% - 15px)',
            right: '-1px',
            width: '0px',
            height: '0px',
            borderTop: '15px solid transparent',
            borderBottom: '15px solid transparent',
            borderRight: '10px solid #232236',
          },
        }),
      },
      '&:hover': {
        bgcolor: '#025B9C',
      },
    };
  };

  const subItemStyles = () => {
    return {
      p: 0,
      flexGrow: 0,
      '& .MuiListItemText-root .MuiTypography-root': {
        color: 'primary.contrastText',
        fontSize: '14px',
        lineHeight: '1.2em',
        fontWeight: 600,
        minHeight: '36px',
        borderBottom: '1px solid',
        borderColor: 'neutral.600',
        p: 1,
        display: 'flex',
        alignItems: 'center',
      },
      '&.Mui-selected': {
        bgcolor: 'transparent',
      },
    };
  };

  const isOutsideLink = !reactRoute && target === '_blank' && route;
  const embedifiedRoute = isOutsideLink ? route : '/embed' + route;
  let path = reactRoute || (route ? embedifiedRoute : undefined);

  // hack because of bad route data
  if (path?.startsWith('/embed/dashboard/fbm/schedule'))
    path = '/embed/fbm/schedule/';
  const isRootPath =
    pathname == '/embed/dashboard' &&
    path?.startsWith('/embed/dashboard/trainer-dashboard');

  const selected =
    subnavOpen ||
    pathname.replace(/\/$/, '') ===
      (reactRoute || embedifiedRoute || '').replace(/\/$/, '') ||
    isRootPath;
  const displayIcon = selected || isHovered ? getSolidIcon(icon) : icon;

  const handleClick = (event) => {
    if (onClick) {
      onClick();
    }
    if (isOutsideLink) {
      return;
    }

    if (path) {
      event.preventDefault();
      startTransition(() => {
        navigate(path);
      });
    }
  };

  const mobileProps = {
    rightAdornment: hasChildrenRoutes ? (
      <Icon color="inherit" size="xsmall" icon={faAngleRight} />
    ) : undefined,
  };

  return (
    <ListItem
      {...(smDown ? mobileProps : {})}
      component={isOutsideLink ? 'a' : Link}
      to={isOutsideLink ? undefined : path}
      href={isOutsideLink ? path : undefined}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      target={target}
      sx={subItem ? subItemStyles() : navItemStyles()}
      primary={title}
      icon={displayIcon}
      selected={selected}
      rel={rel}
      clickable
      // To force embed page to update when navigating
      // between pages that are both on the embed route
      state={
        target === '_blank'
          ? undefined
          : {
              renderTime: Date.now(),
            }
      }
    />
  );
};

const AdminNavItems = ({
  setActiveSubmenu,
  activeSubmenu,
}: {
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  activeSubmenu: DecoratedNavItem | null;
}) => {
  const adminNavItems = useAdminNav();

  const renderNavItems = (items: DecoratedNavItem[], level = 0) => {
    return items.map((navItem, i) => {
      if (!navItem.enabled) return null;
      const hasChildrenRoutes = !!navItem?.children?.length;
      return (
        <TopLevelNavItem
          key={navItem.id + i}
          title={navItem.name}
          icon={level === 0 ? navItem.icon : undefined}
          subnavOpen={activeSubmenu?.id === navItem.id}
          route={hasChildrenRoutes ? undefined : navItem.route}
          reactRoute={hasChildrenRoutes ? undefined : navItem.reactRoute}
          onClick={() => {
            if (hasChildrenRoutes) {
              setActiveSubmenu(navItem);
              return;
            }
            setActiveSubmenu(null);
          }}
          target={navItem.target}
          rel={navItem.rel}
          level={level}
        ></TopLevelNavItem>
      );
    });
  };

  return (
    <List sx={{ m: 0, width: '100%' }}>{renderNavItems(adminNavItems)}</List>
  );
};

const MainNavItems = ({
  setActiveSubmenu,
  activeSubmenu,
  customNavItems,
}: {
  setActiveSubmenu: (id: DecoratedNavItem | null) => void;
  activeSubmenu: DecoratedNavItem | null;
  customNavItems?: DecoratedNavItem[];
}) => {
  const mainNavItems = useMainNav();
  const navItemsToRender = customNavItems ? customNavItems : mainNavItems;

  const renderNavItems = (items: DecoratedNavItem[], level = 0) => {
    return items.map((navItem, i) => {
      if (!navItem.enabled) return null;
      const hasChildrenRoutes = !!navItem?.children?.length;
      return (
        <TopLevelNavItem
          key={navItem.id + i}
          title={navItem.name}
          icon={level === 0 ? navItem.icon : undefined}
          subnavOpen={activeSubmenu?.id === navItem.id}
          route={hasChildrenRoutes ? undefined : navItem.route}
          reactRoute={hasChildrenRoutes ? undefined : navItem.reactRoute}
          onClick={() => {
            if (hasChildrenRoutes) {
              setActiveSubmenu(navItem);
              return;
            }
            setActiveSubmenu(null);
          }}
          target={navItem.target}
          rel={navItem.rel}
          level={level}
        ></TopLevelNavItem>
      );
    });
  };

  return (
    <List sx={{ m: 0, width: '100%' }}>{renderNavItems(navItemsToRender)}</List>
  );
};
